// src/config/sessionPolicy.js
/**
 * RD-SESS-001 — SINGLE SOURCE OF TRUTH for session timing.
 * Change a number HERE, nowhere else. Mirrors the spec table:
 *   docs/superpowers/specs/2026-06-24-rd-sess-001-session-expiration-design.md
 *
 * Two enforcement layers:
 *   - Client (this file): per-role IDLE timeouts + the warning/refresh/grace/poll timers.
 *   - Server (Supabase dashboard): the absolute cap + inactivity + token TTL — see SERVER_SESSION
 *     (documented here, NOT read by client code; set per environment in Authentication → Sessions).
 */

import { IS_DEBUG_BUILD } from '../utils/env/debugBuild';

const SEC = 1000;
const MIN = 60 * SEC;
const HOUR = 60 * MIN;

/** Per-role idle timeout (inactivity → auto sign-out), in ms. */
export const IDLE_TIMEOUT_MS = {
  platformSuperAdmin: 1 * HOUR, // only while on /platform/*
  admin: 4 * HOUR,
  billing: 4 * HOUR,
  // Operations: office seat (dispatcher), same cadence as admin/billing.
  operations: 4 * HOUR,
  supervisor: 8 * HOUR,
  worker: 8 * HOUR,
};

/** "Stay signed in?" warning lead time before the idle timeout, in ms. */
export const IDLE_WARNING_LEAD_MS = 2 * MIN;

/**
 * Dev-only idle test switch (KAN-608): `?idletest=1` sets a per-tab flag
 * (sessionStorage `cv_idle_test`), `?idletest=0` clears it. While set, the idle
 * timeout is capped at 60s and the warning lead at 30s for EVERY role, so the
 * "Still there?" flow can be watched on a device in a minute. It can ONLY
 * shorten (Math.min), never lengthen. Gated on IS_DEBUG_BUILD (localhost + the
 * dev site) — completely inert on the production build: neither the URL nor a
 * stale flag is read there.
 */
export const IDLE_TEST_STORAGE_KEY = 'cv_idle_test';
export const IDLE_TEST_TIMEOUT_MS = 60 * SEC;
export const IDLE_TEST_WARNING_LEAD_MS = 30 * SEC;

export function idleTestActive() {
  if (!IS_DEBUG_BUILD || typeof window === 'undefined') return false;
  try {
    const q = window.location.search || '';
    if (/[?&]idletest=0(&|$)/.test(q)) window.sessionStorage.removeItem(IDLE_TEST_STORAGE_KEY);
    else if (/[?&]idletest=1(&|$)/.test(q)) window.sessionStorage.setItem(IDLE_TEST_STORAGE_KEY, '1');
    return window.sessionStorage.getItem(IDLE_TEST_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Warning lead time, honoring the dev-only test switch (only ever shorter). */
export function getIdleWarningLeadMs() {
  return idleTestActive() ? Math.min(IDLE_WARNING_LEAD_MS, IDLE_TEST_WARNING_LEAD_MS) : IDLE_WARNING_LEAD_MS;
}

/** On app resume, refresh the access token if within this window of expiry, in ms. */
export const RESUME_REFRESH_SKEW_MS = 60 * SEC;

/**
 * On app resume, throttle window for the GoTrue-validated session-liveness
 * probe (2026-07-27 revoked-session-death-detection fix). PostgREST/
 * Realtime/Storage only check the JWT signature, so a revoked-but-not-yet-
 * -expired device would otherwise keep DB access until the access token
 * expires (see jwt_expiry in supabase/config.toml). Resume is the
 * highest-value moment to check -- a backgrounded device would otherwise
 * ride the JWT to its full TTL before self-healing. Throttled so rapid
 * tab-switches/focus events don't spam an extra auth call each time.
 */
export const SESSION_LIVENESS_PROBE_THROTTLE_MS = 60 * SEC;

/**
 * ⚠️ NO LONGER USED TO END A SESSION — kept as documentation only (2026-07-31).
 *
 * This used to mean "reconnect within this window keeps the session; beyond it →
 * expire". `AuthContext.handleOnline` acted on it literally: past the window it
 * nulled all session state and showed "Please sign in again" WITHOUT asking the
 * server, and without calling signOut() — so a token still valid for hours was
 * discarded from the UI while remaining live in storage. A laptop asleep over
 * lunch was enough to trigger it. That was the "random logouts" report.
 *
 * The comment below is preserved because its instinct was right and its fix was
 * not: it had already noticed that a SHORT grace signs field workers out for
 * ordinary bad reception, and responded by lengthening the grace. But no grace
 * length is correct, because time offline is not evidence about a session. The
 * handler now always asks the server on reconnect and lets the answer decide —
 * so there is nothing left to tune here.
 *
 * DO NOT re-wire this into a sign-out path. If you need a reconnect behaviour,
 * gate it on the SERVER's answer, never on elapsed offline time.
 *
 * ── original rationale, kept for context ──
 * Deliberately NOT tied to the access-token TTL (it used to say "matches" it,
 * which stopped being true when jwt_expiry dropped 3600s → 900s). Reconnecting
 * is governed by the REFRESH token, not the access token, so a short access TTL
 * does not require a short grace. Keep this generous: CrewVex is a field app —
 * workers lose signal at pools, in garages and behind houses, and a 15-minute
 * grace would sign them out mid-route for ordinary bad reception.
 */
export const OFFLINE_GRACE_MS = 1 * HOUR;

/**
 * Membership/tenant deactivation + role-drift poll interval, in ms (KAN-1044: 5 -> 15 min).
 * BACKSTOP only: the membership-role-watch + session-revocation realtime channels, the
 * company-access ping and the PostgREST live-session gate are the live paths. The poll
 * is paused while the tab is hidden and runs once on resume (see MIN_CHECK_GAP below).
 */
export const DEACTIVATION_POLL_MS = 15 * MIN;
/** On resume the poll runs once only if the last check is at least this old. */
export const DEACTIVATION_RESUME_MIN_GAP_MS = 60 * 1000;

/**
 * Server-enforced limits — set in the Supabase dashboard (Authentication → Sessions),
 * GLOBAL per environment (Dev + Prod separately). Documented here so dashboard + code
 * share one table; NOT consumed by client code.
 */
export const SERVER_SESSION = {
  absoluteCapHours: 24, // "Time-box user sessions"
  inactivityHours: 8,   // "Inactivity timeout"
  accessTokenTtlMin: 60, // JWT expiry — supabase/config.toml `jwt_expiry = 3600`
};

/**
 * Idle timeout for a role + platform context. Platform super-admins get the 1h ceiling
 * ONLY on /platform/* routes; on tenant routes they fall through to the tenant tier.
 */
export function getIdleTimeoutMs(role, isSuperAdmin) {
  const onPlatformRoute =
    typeof window !== 'undefined' &&
    typeof window.location?.pathname === 'string' &&
    window.location.pathname.startsWith('/platform');
  const base = (isSuperAdmin && onPlatformRoute)
    ? IDLE_TIMEOUT_MS.platformSuperAdmin
    // Unknown/absent role falls back to the admin tier.
    : (IDLE_TIMEOUT_MS[role] ?? IDLE_TIMEOUT_MS.admin);
  return idleTestActive() ? Math.min(base, IDLE_TEST_TIMEOUT_MS) : base;
}
